import { DateString } from 'ComponentLibrary/src/common/date'
import { MonetaryValueDataObjectType } from 'ComponentLibrary/src/common/money'
import { getColumnDefDCF } from 'ComponentLibrary/src/molecules/Table/DCF/ColumnsDCF'
import { DEFAULT_DCF_TABLE_COPY } from 'ComponentLibrary/src/molecules/Table/DCF/TableDCF'
import type { RowDCF, DCFTableCopy, DCFTableProps } from 'ComponentLibrary/src/molecules/Table/DCF/TableDCF'

export function mapDCFResultsSchemaToTableDCF(
    dcfResults: DiscountedCashFlowMappingResultSchema,
    copy: DCFTableCopy = DEFAULT_DCF_TABLE_COPY
): DCFTableProps {
    const firstPeriod = dcfResults.periods[0]

    const tableData = [
        ...getMarketRentCashflowRows(dcfResults, firstPeriod),
        ...getRevenueCashflowRows(dcfResults, firstPeriod, copy),
        ...getCostCashflowRows(dcfResults, firstPeriod, copy),
        getNetCashflowTotalRow(dcfResults, copy),
        getDiscountFactorRow(dcfResults, copy),
        getPresentValueRow(dcfResults, copy),
    ]
    return {
        columns: getColumnDefDCF(tableData),
        data: tableData,
        terminalValue: dcfResults.exit_period.terminal_value,
        presentValue: dcfResults.present_value,
        presentValueWithTransactionCost: dcfResults.present_value_with_transaction_cost,
        assetValueRounded: dcfResults.asset_value_rounded,
        copy,
    }
}

function getCashflowRows(
    dcfResults: DiscountedCashFlowMappingResultSchema,
    firstPeriod: DcfPeriodSchema,
    category: Extract<RowDCF['category'], 'market_rent_cashflow' | 'revenue_cashflow' | 'cost_cashflows'>
): RowDCF[] {
    let cashflowRows: RowDCF[] = []

    const cashflowSubCategories = firstPeriod[category] && Object.keys(firstPeriod[category]?.values)
    if (cashflowSubCategories) {
        cashflowRows = cashflowSubCategories.map((subcategory) => ({
            category,
            subcategory,
            periods: dcfResults.periods.map((dcfResultSchemaPeriod) => ({
                date: dcfResultSchemaPeriod.start_date,
                value: dcfResultSchemaPeriod[category]?.values[subcategory].total,
            })),
            exitPeriod: {
                date: dcfResults.exit_period.start_date,
                value: dcfResults.exit_period[category]?.values[subcategory].total,
            },
            subRows:
                firstPeriod[category]?.values[subcategory] &&
                Object.keys(firstPeriod[category]?.values[subcategory].sub_cashflows).map((subCashflowItemKey) => ({
                    category,
                    subcategory: subCashflowItemKey,
                    periods: dcfResults.periods.map((dcfResultSchemaPeriod) => ({
                        date: dcfResultSchemaPeriod.start_date,
                        value: dcfResultSchemaPeriod[category]?.values[subcategory].sub_cashflows[subCashflowItemKey],
                    })),
                    exitPeriod: {
                        date: dcfResults.exit_period.start_date,
                        value: dcfResults.exit_period[category]?.values[subcategory].sub_cashflows[subCashflowItemKey],
                    },
                })),
        }))
    }

    return cashflowRows
}

function getMarketRentCashflowRows(dcfResults: DiscountedCashFlowMappingResultSchema, firstPeriod: DcfPeriodSchema): RowDCF[] {
    return getCashflowRows(dcfResults, firstPeriod, 'market_rent_cashflow')
}

function getRevenueCashflowRows(
    dcfResults: DiscountedCashFlowMappingResultSchema,
    firstPeriod: DcfPeriodSchema,
    copy: DCFTableCopy
): RowDCF[] {
    const category = 'revenue_cashflow'
    const cashflowRows = getCashflowRows(dcfResults, firstPeriod, category)

    cashflowRows.push({
        category,
        subcategory: copy.rows.grossRevenue,
        isTotal: true,
        periods: dcfResults.periods.map((dcfResultSchemaPeriod) => ({
            date: dcfResultSchemaPeriod.start_date,
            value: dcfResultSchemaPeriod[category]?.total,
        })),
        exitPeriod: {
            date: dcfResults.exit_period.start_date,
            value: dcfResults.exit_period[category]?.total,
        },
    })
    return cashflowRows
}

function getCostCashflowRows(
    dcfResults: DiscountedCashFlowMappingResultSchema,
    firstPeriod: DcfPeriodSchema,
    copy: DCFTableCopy
): RowDCF[] {
    const category = 'cost_cashflows'
    const cashflowRows = getCashflowRows(dcfResults, firstPeriod, category)
    cashflowRows.push({
        category,
        subcategory: copy.rows.totalCosts,
        isTotal: true,
        periods: dcfResults.periods.map((dcfResultSchemaPeriod) => ({
            date: dcfResultSchemaPeriod.start_date,
            value: dcfResultSchemaPeriod[category]?.total,
        })),
        exitPeriod: {
            date: dcfResults.exit_period.start_date,
            value: dcfResults.exit_period[category]?.total,
        },
    })

    return cashflowRows
}

function getDiscountFactorRow(dcfResults: DiscountedCashFlowMappingResultSchema, copy: DCFTableCopy): RowDCF {
    return {
        category: 'discount_factor',
        subcategory: copy.rows.discountFactor,
        periods: dcfResults.periods.map((dcfResultSchemaPeriod) => ({
            date: dcfResultSchemaPeriod.start_date,
            value: dcfResultSchemaPeriod.discount_factor,
        })),
        exitPeriod: {
            date: dcfResults.exit_period.start_date,
            value: dcfResults.exit_period.discount_factor,
        },
    }
}

function getNetCashflowTotalRow(dcfResults: DiscountedCashFlowMappingResultSchema, copy: DCFTableCopy): RowDCF {
    const category = 'net_cashflow'
    return {
        category,
        subcategory: copy.rows.totalNetRevenue,
        isTotal: true,
        periods: dcfResults.periods.map((dcfResultSchemaPeriod) => ({
            date: dcfResultSchemaPeriod.start_date,
            value: dcfResultSchemaPeriod[category],
        })),
        exitPeriod: {
            date: dcfResults.exit_period.start_date,
            value: dcfResults.exit_period[category],
        },
    }
}

function getPresentValueRow(dcfResults: DiscountedCashFlowMappingResultSchema, copy: DCFTableCopy): RowDCF {
    const category = 'present_value'
    return {
        category,
        subcategory: copy.rows.presentValue,
        isTotal: true,
        periods: dcfResults.periods.map((dcfResultSchemaPeriod) => ({
            date: dcfResultSchemaPeriod.start_date,
            value: dcfResultSchemaPeriod[category],
        })),
        exitPeriod: {
            date: dcfResults.exit_period.start_date,
            value: dcfResults.exit_period[category],
        },
    }
}

// The types are duplicated to avoid duplicating the mapping logic TODO: WEB-3196

export type CreatePeriodsGroup = {
    source: DcfPeriodSchema[]
}

export type ExistingPeriodsGroup = CreatePeriodsGroup & {
    yearly: DcfPeriodSchema[]
}

export type CreateExitPeriodGroup = {
    source: ExitPeriodSchema
}

export type ExistingExitPeriodGroup = CreateExitPeriodGroup & {
    yearly: ExitPeriodSchema
}

export type BaseDiscountedCashFlowResultSchema<T extends 'create' | 'read'> = {
    periods: T extends 'create' ? CreatePeriodsGroup : ExistingPeriodsGroup
    exit_period: T extends 'create' ? CreateExitPeriodGroup : ExistingExitPeriodGroup
    present_value: MonetaryValueDataObjectType
    present_value_with_transaction_cost?: MonetaryValueDataObjectType
    asset_value_rounded: MonetaryValueDataObjectType
    kpis: DiscountedCashflowKpiSchema
}

export type CreateDiscountedCashFlowResultSchema = BaseDiscountedCashFlowResultSchema<'create'>

export type ExistingDiscountedCashFlowResultSchema = BaseDiscountedCashFlowResultSchema<'read'>

export type DiscountedCashFlowMappingResultSchema = {
    periods: DcfPeriodSchema[]
    exit_period: ExitPeriodSchema
    present_value: MonetaryValueDataObjectType
    present_value_with_transaction_cost?: MonetaryValueDataObjectType
    asset_value_rounded: MonetaryValueDataObjectType
    kpis: DiscountedCashflowKpiSchema
}

export type DiscountedCashflowKpiSchema = {
    discount_rate: number
    capitalization_rate: number
    net_initial_yield_on_reasonable_rent: number
    gross_initial_yield_on_reasonable_rent: number
    gross_multiplier: number
}

export type DcfPeriodSchema = {
    start_date: DateString
    market_rent_cashflow?: RevenueCashflowSchema
    revenue_cashflow: RevenueCashflowSchema
    cost_cashflows: CostCashflowSchema
    net_cashflow: MonetaryValueDataObjectType
    discount_factor: number
    present_value: MonetaryValueDataObjectType
}

export type ExitPeriodSchema = {
    terminal_value: MonetaryValueDataObjectType
    end_date?: DateString
} & DcfPeriodSchema

export type RevenueCashflowSchema = {
    total: MonetaryValueDataObjectType
    values: Record<string, MainCashflowItem>
}

export type MainCashflowItem = {
    title: string
    total: MonetaryValueDataObjectType
    sub_cashflows: Record<string, MonetaryValueDataObjectType>
}

export type CostCashflowSchema = {
    total: MonetaryValueDataObjectType
    values: Record<string, MainCostCashflowItem>
}

export type MainCostCashflowItem = {
    category: 'capex' | 'opex'
} & MainCashflowItem
